<template>
	<div class="payment-methods-page" v-if="isReady">
		<div class="payment-method-actions">
			<button v-if="achEnabled" type="button" class="portal-button outline" @click="linkNewAccount">
				<portal-icon name="plus" :size="17"></portal-icon>
				{{ $t('invoices.ach-account.new-account') }}
			</button>
			<button type="button" class="portal-button primary" @click="managePaymentMethods">
				<portal-icon name="settings" :size="17"></portal-icon>
				{{ $t('invoices.ach-account.manage-payment-methods') }}
			</button>
		</div>

		<div class="payment-method-table-shell portal-compact-list-shell">
			<v-data-table
				class="portal-compact-table payment-method-table"
				:headers="headers"
				:hide-default-footer="true"
				:disable-pagination="true"
				:mobile-breakpoint="0"
				:items="accounts"
				:no-data-text="$t('invoices.viewer.link-account')"
			>
				<template v-slot:item.label="{ item }">
					<div class="payment-method-name">
						<span class="method-icon" aria-hidden="true">
							<portal-icon :name="item.type === 'card' ? 'credit-card' : 'landmark'" :size="18"></portal-icon>
						</span>
						<span class="portal-cell-truncate">{{ normalizedLabel(item) }}</span>
					</div>
				</template>

				<template v-slot:item.routing="{ item }">
					<span class="portal-cell-muted">{{ item.routing || '--' }}</span>
				</template>

				<template v-slot:item.last4="{ item }">
					<span class="account-ending">•••• {{ item.last4 }}</span>
				</template>

				<template v-slot:item.status="{ item }">
					<portal-status-pill
						v-if="item.status === 'verified'"
						background="var(--v-success_10-base)"
						color="var(--v-success-base)"
					>
						{{ item.status }}
					</portal-status-pill>
					<button v-else type="button" class="verify-action" @click.stop="verifyAccount(item)">
						<portal-icon name="alert-circle" :size="15"></portal-icon>
						{{ $t('global.verify') }}
					</button>
				</template>

				<template v-slot:item.id="{ item }">
					<button
						v-if="item.type === 'bank_account'"
						type="button"
						class="row-action danger"
						:aria-label="$t('global.delete')"
						@click.stop="deleteAccount(item)"
					>
						<portal-icon name="trash-2" :size="17"></portal-icon>
					</button>
				</template>
			</v-data-table>
		</div>
	</div>
</template>

<script>
	import PaymentsService from '@/modules/invoices/PaymentsService';
	import ConfirmModal from '@/components/ConfirmModal';
	import NewAchAccount from '@/modules/invoices/NewAchAccount';
	import ManualAchAccount from '@/modules/invoices/ManualAchAccount';
	import VerifyAchAccount from '@/modules/invoices/VerifyAchAccount';
	import SubscriptionService from '@/modules/subscriptions/SubscriptionService';
	import PortalIcon from '@/components/PortalIcon.vue';
	import PortalStatusPill from '@/components/PortalStatusPill.vue';

	export default {
		name: 'AchAccounts',

		props: ['achEnabled'],

		components: { PortalIcon, PortalStatusPill },

		data: function() {
			return {
				accounts: [],
				paymentsService: new PaymentsService(),
        subscriptionService: new SubscriptionService(),
				token: null,
				isReady: false,

				headers: [
          { text: this.$t('invoices.ach-account.bank-name'), value: 'label' },
          { text: this.$t('invoices.ach-account.routing-number'), value: 'routing' },
					{ text: this.$t('invoices.ach-account.account'), value: 'last4' },
					{ text: this.$t('invoices.ach-account.status'), value: 'status' },
					{ text: '', value: 'id' },
				],
			};
		},

		mounted() {
			this.getAccounts();
		},

		beforeDestroy() {},

		methods: {
			normalizedLabel(item) {
				return item && item.label ? item.label.toLowerCase() : '';
			},

			getAccounts: function() {
				this.paymentsService.getPaymentMethods().then((res) => {
					this.accounts.splice(0, this.accounts.length);
					this.accounts.push(...res.data);
					this.isReady = true;
				});
			},


      verifyAccount: function(bankAccount){
        let binding = {
          bankAccount: bankAccount
        };
        this.$store.state.globalModalController.openModal(VerifyAchAccount, binding, true, false).then((res) => {
          if(res) {
            this.getAccounts();
          }
        });
      },


			linkNewAccount: function() {
				this.$store.state.globalModalController.openModal(NewAchAccount, {}, true, false).then((res) => {
					if (res === 'PLAID') {
						this.linkWithPlaid();
					} else if (res === 'MANUAL') {
						this.$store.commit('startLoading');
						setTimeout(() => {
							this.$store.commit('stopLoading');
							this.openManualAchInput();
						}, 1000);
					}
				});
			},

			openManualAchInput: function() {
				this.$store.state.globalModalController.openModal(ManualAchAccount, {}, true, false).then((res) => {
					if (res) {
						this.accounts.push(res);
            this.confirmMicroDeposits();
					}
				});
			},

      confirmMicroDeposits: function(){
        let binding = {
          headingText: this.$t('invoices.ach-account.action-required'),
          hideNo: true,
          yesText: this.$t('global.ok'),
          bodyText: this.$t('invoices.ach-account.stripe-deposits'),
        };
        this.$store.state.globalModalController.openModal(ConfirmModal, binding, false, false);
      },

			linkWithPlaid: function() {
				this.$store.commit('startLoading');
				this.paymentsService.createACHLinkSession(window.location.href).then((res) => {
					this.$store.commit('stopLoading');
					console.log('ACH Link Session created', res.data);
					window.location.href = res.data.linkUri;
				});
			},

			deleteAccount: function(account) {
				let binding = {
					headingText: this.$t('global.confirm'),
					bodyText: this.$t('invoices.ach-account.confirm-ach'),
				};
				this.$store.state.globalModalController.openModal(ConfirmModal, binding, false, false).then((res) => {
					if (res) {
						this.$store.commit('startLoading');
						this.paymentsService
							.deleteBankAccount(account.id)
							.then(() => this.getAccounts())
							.finally(() => this.$store.commit('stopLoading'));
					}
				});
			},

      managePaymentMethods: function(){
        this.subscriptionService.getSubscriptionPortalLink(document.location.href).then((res) => {
          document.location.href = res.data;
        })
      }
		},

		computed: {},
	};
</script>

<style scoped lang="scss">
	.payment-methods-page {
		display: grid;
		gap: 14px;
		padding: 16px;
	}

	.payment-method-actions {
		display: flex;
		justify-content: flex-end;
		flex-wrap: wrap;
		gap: 9px;
	}

	.portal-button {
		min-height: 42px;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		gap: 8px;
		padding: 0 14px;
		border: 1px solid var(--portal-border);
		border-radius: var(--brand-button-radius, 11px);
		font: inherit;
		font-size: 13px;
		font-weight: 650;
		cursor: pointer;
	}

	.portal-button.outline {
		background: var(--v-white-base);
		color: var(--v-gray_90-base);
	}

	.portal-button.primary {
		border-color: var(--v-primary-base);
		background: var(--v-primary-base);
		color: var(--v-white-base);
	}

	.payment-method-table-shell {
		min-width: 0;
		overflow: hidden;
		border-radius: var(--portal-radius-md);
	}

	.payment-method-name {
		min-width: 0;
		display: flex;
		align-items: center;
		gap: 10px;
		text-transform: capitalize;
	}

	.method-icon {
		width: 34px;
		height: 34px;
		flex: 0 0 34px;
		display: grid;
		place-items: center;
		border-radius: 10px;
		background: color-mix(in srgb, var(--v-primary-base) 8%, var(--portal-surface));
		color: var(--v-primary-base);
	}

	.account-ending {
		color: var(--portal-text);
		font-weight: 620;
		white-space: nowrap;
	}

	.verify-action,
	.row-action {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		border: 0;
		font: inherit;
		cursor: pointer;
	}

	.verify-action {
		min-height: 30px;
		gap: 6px;
		padding: 5px 9px;
		border-radius: 999px;
		background: var(--v-warning_10-base);
		color: var(--v-warning-base);
		font-size: 12px;
		font-weight: 650;
	}

	.row-action {
		width: 34px;
		height: 34px;
		margin-left: auto;
		border-radius: var(--brand-button-radius, 10px);
		background: transparent;
		color: var(--portal-muted);
	}

	.row-action:hover {
		background: var(--v-error_10-base);
		color: var(--v-error-base);
	}

	@container (max-width: 690px) {
		.payment-method-table ::v-deep th:nth-child(2),
		.payment-method-table ::v-deep td:nth-child(2) {
			display: none;
		}
	}

	@container (max-width: 510px) {
		.payment-method-table ::v-deep th:nth-child(3),
		.payment-method-table ::v-deep td:nth-child(3) {
			display: none;
		}
	}

	@media (max-width: 599px) {
		.payment-methods-page {
			padding: 12px;
		}

		.payment-method-table ::v-deep th:nth-child(2),
		.payment-method-table ::v-deep td:nth-child(2),
		.payment-method-table ::v-deep th:nth-child(3),
		.payment-method-table ::v-deep td:nth-child(3) {
			display: none;
		}

		.payment-method-table ::v-deep table {
			min-width: 0 !important;
			width: 100% !important;
		}

		.payment-method-actions {
			display: grid;
			grid-template-columns: 1fr;
		}

		.portal-button {
			width: 100%;
		}
	}
</style>
